import HashTagIcon from '@assets/images/icons/hashtag.svg?react'
import type { Placement } from '@popperjs/core/lib/enums'
import type React from 'react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { type StrictModifier, usePopper } from 'react-popper'
import { useClickAway, useDebounce, useUnmount } from 'react-use'

import css from './ColorPicker.module.scss'

type Props = {
  colorHex: string
  position?: Placement
  colorPikerAreaRef: HTMLDivElement | null
  offset?: {
    x?: number
    y?: number
  }
  onSetColor?: (hexCode: string) => void
  onOutsideClick: () => void
  onColorChange?: (hexCode: string) => void
}

export const ColorPicker: React.FC<Props> = ({
  colorHex,
  position,
  colorPikerAreaRef,
  offset,
  onSetColor,
  onOutsideClick,
  onColorChange,
}) => {
  const colorWithoutHash = colorHex.replace('#', '')

  const [currentColor, setCurrentColor] = useState(colorWithoutHash)
  const [inputValue, setInputValue] = useState(colorWithoutHash)
  const [modifiers, setModifiers] = useState<StrictModifier[]>([])
  const [pickerContainer, setPickerContainer] = useState<HTMLDivElement | null>(
    null
  )

  const inputRef = useRef<HTMLInputElement>(null)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  useClickAway(colorPickerRef, onOutsideClick)
  useUnmount(onOutsideClick)

  useEffect(() => {
    const x = offset?.x !== undefined ? offset.x : 0
    const y = offset?.y !== undefined ? offset.y : 8

    setModifiers((prevState) => {
      return [
        ...prevState,
        {
          name: 'offset',
          options: {
            offset: [x, y],
          },
        },
      ]
    })
  }, [offset])

  useEffect(() => {
    inputRef?.current?.select()
  }, [])

  const { styles, attributes } = usePopper(colorPikerAreaRef, pickerContainer, {
    modifiers,
    placement: position || 'bottom-start',
  })

  useDebounce(
    () => {
      if (onColorChange) {
        handleColor(onColorChange)
      }
    },
    100,
    [currentColor]
  )

  const handleColor = (fn: (hexCode: string) => void) => {
    fn(`#${currentColor}`)
  }

  const handleSetColor = (value: string) => {
    const upperValue = value.toUpperCase().replace('#', '')
    setCurrentColor(upperValue)
    setInputValue(upperValue)
  }

  const processInput = (input: string) => {
    const hexCharacters = input.replace(/[^0-9A-Fa-f]/g, '').toUpperCase()

    switch (hexCharacters.length) {
      case 1:
        return hexCharacters.repeat(6) // A -> #AAAAAA
      case 2:
        return hexCharacters.repeat(3) // AB -> #ABABAB
      case 3:
        return hexCharacters
          .split('')
          .map((char) => char + char)
          .join('') // ABC -> #AABBCC
      case 4:
        return hexCharacters + hexCharacters[2] + hexCharacters[3] // ABCD -> #ABCDCD
      case 5:
        return hexCharacters + hexCharacters[4]
      case 6:
        return hexCharacters // ABCDEF -> #ABCDEF
      default:
        return hexCharacters.length > 6
          ? hexCharacters.slice(0, 6)
          : colorHex.replace('#', '')
    }
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()

    const newValue = e.target.value
    const processedHex = processInput(newValue)

    setInputValue(newValue)
    setCurrentColor(processedHex)
  }

  const handleOnBlur = () => {
    const processedHex = processInput(inputValue)

    setInputValue(processedHex)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation()

    if (e.key === 'Enter' && onSetColor) {
      handleColor(onSetColor)
    }
  }

  return (
    <div
      className={css.container}
      ref={setPickerContainer}
      style={styles.popper}
      {...attributes.popper}
    >
      <div ref={colorPickerRef} onKeyDown={handleKeyDown}>
        <HexColorPicker color={currentColor} onChange={handleSetColor} />
        <div className={css.inputWrap}>
          <span className={css.hashTag}>
            <HashTagIcon />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            placeholder="Hex code"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
          />
          {onSetColor && (
            <div className={css.button} onClick={() => handleColor(onSetColor)}>
              Save
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
